// The walkthrough (decisions 47 and 49): driver.js, bundled, in the planner's look. It runs once
// per browser on the plan page and again from the footer's "Show me around". It reads and writes
// one key, `sourdough.v1.guide`, and nothing else: no number, step id or stored plan moves.
import { createElement, type ReactElement } from "react";
import { driver, type Config, type DriveStep, type Driver, type PopoverDOM } from "driver.js";
import "driver.js/dist/driver.css";
import "./guide.css";
import { DEFAULT_GUIDE, KEYS, isGuideState, load, save, type GuideState } from "../lib/storage";
import { TOUR, guideSelector, type GuideTarget } from "./copy";

/** What this browser has seen of the guide (`KEYS.guide`, decision 49); the default when unset. */
export function loadGuide(): GuideState {
  return load(KEYS.guide, DEFAULT_GUIDE, isGuideState);
}

export function saveGuide(next: GuideState): void {
  save(KEYS.guide, next);
}

/** A target on the page and laid out (not inside something closed or `display: none`). */
export function findTarget(target: GuideTarget): Element | undefined {
  const el = document.querySelector(guideSelector(target));
  return el && el.getClientRects().length > 0 ? el : undefined;
}

/**
 * The popover in the planner's look: `.sd-pop` (guide.css) does the colours; this names the
 * dialog for screen readers, which driver.js 1.3.1 does not.
 */
export function themePopover(popover: PopoverDOM): void {
  const { wrapper, title, description, closeButton } = popover;
  wrapper.setAttribute("role", "dialog");
  wrapper.setAttribute("aria-labelledby", title.id);
  wrapper.setAttribute("aria-describedby", description.id);
  closeButton.setAttribute("aria-label", "Close the guide");
}

/**
 * What every driver here shares: the look, the stage around the lit element, and closing by the
 * ×, a click on the dim, or Escape. Hint cards (G2) start from this too.
 */
export function baseConfig(): Config {
  return {
    popoverClass: "sd-pop",
    stagePadding: 6,
    stageRadius: 14,
    allowClose: true,
    allowKeyboardControl: true,
    overlayColor: "#140e08",
    overlayOpacity: 0.55,
    onPopoverRender: (popover) => themePopover(popover),
  };
}

/** driver.js keeps one state for the page, so one guide at a time: the open one closes first. */
let active: Driver | undefined;

/** Closes whatever the guide has open (the tour, a hint card). */
export function stopGuide(): void {
  if (active?.isActive()) active.destroy();
  active = undefined;
}

/**
 * Runs `steps` from step `from` with the base config and `extra`, after closing whatever was
 * open. A hint card's "show me" starts on its second step, so Back returns to the card.
 */
export function runGuide(steps: DriveStep[], extra: Config = {}, from = 0): Driver {
  stopGuide();
  const d = driver({ ...baseConfig(), ...extra, steps });
  active = d;
  d.drive(from);
  return d;
}

/**
 * The walkthrough's steps for this page. driver.js 1.3.1 has no option to skip a missing
 * element (it centres the popover on a dummy instead), so a step whose target is absent, such as
 * the view toggle on a one-dough plan or the account menu with accounts off, is left out here.
 */
export function tourSteps(): DriveStep[] {
  return TOUR.flatMap((step) => {
    const element = findTarget(step.target);
    return element ? [{ element, popover: { title: step.title, description: step.body } }] : [];
  });
}

/** Starts the walkthrough. False when nothing it points at is on the page. */
export function startTour(): boolean {
  const steps = tourSteps();
  if (steps.length === 0) return false;
  // Seen from the moment it starts: driver.js animates each step for 400 ms, and a close inside
  // that window can skip onDestroyed's write, which ran the tour again on the next load.
  saveGuide({ ...loadGuide(), seen: true });
  runGuide(steps, {
    showProgress: true,
    progressText: "{{current}} of {{total}}",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    // Finished, closed by the ×, the dim or Escape: seen either way (recorded again here).
    onDestroyed: () => {
      saveGuide({ ...loadGuide(), seen: true });
      active = undefined;
    },
  });
  return true;
}

/**
 * The first visit (decision 49): the walkthrough once, on the plan page, never in kitchen mode.
 * Call it after the plan page has rendered. True when it started the tour.
 */
export function firstRun({ view, kitchen }: { view: string; kitchen: boolean }): boolean {
  if (view !== "plan" || kitchen) return false;
  if (active?.isActive()) return false; // StrictMode runs effects twice in development.
  if (loadGuide().seen) return false;
  return startTour();
}

/** The footer's "Show me around". */
export function GuideButton(): ReactElement {
  return createElement(
    "button",
    { type: "button", className: "guide-around", onClick: () => startTour() },
    "Show me around",
  );
}
